import { Observer, UsersResponse, convertObserver } from '@nextforce/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useServer } from 'app/server/context'
import { produce } from 'immer'
import { convertUserRoleToQueryParam } from './shared/convert'
import keys from './shared/keys'
import { AddEditUserQueryParams } from './shared/types'

export function useEditUser() {
  const { server } = useServer()
  const client = useQueryClient()

  return useMutation({
    mutationFn: editUser,
    onSuccess: async (user, { role, companyId }) => {
      const userListData = client.getQueryData<Observer[]>(
        keys.list(companyId, role)
      )

      if (userListData) {
        client.setQueryData<Observer[]>(
          keys.list(companyId, role),
          produce(userListData, updatedData => {
            const index = userListData.findIndex(d => d.id === user.id)
            if (index === -1) {
              updatedData.push(user)
            } else {
              updatedData[index] = user
            }
          })
        )
        client.setQueryData<Observer>(keys.detail(companyId, user.id), user)
      }
    },
  })

  async function editUser(props: AddEditUserQueryParams): Promise<Observer> {
    const response = await server.put<UsersResponse.Observer>(
      getUrl(props),
      getBody(props)
    )
    return convertObserver(response)
  }
}

function getUrl({ userId, storeIds, role, companyId }: AddEditUserQueryParams) {
  if (!companyId) {
    throw new Error('Company id invalid')
  }
  if (role === 'observer') {
    return `/v2/users/observers/${companyId}/${userId}?store_ids=${
      storeIds?.length ? storeIds : ''
    }`
  } else if (role === 'staff' || role === 'guest') {
    return `/v2/users/${userId}`
  }
  throw new Error('Role invalid')
}

function getBody(props: AddEditUserQueryParams) {
  return {
    first_name: props.firstName,
    last_name: props.lastName,
    phone_number: props.phoneNumber,
    role: convertUserRoleToQueryParam(props.role),
    job_title: props.jobTitle,
  }
}
